import { useMemo } from 'react'
import { useQuery as useQueryBase } from '@maui/react-query'
import { useMatiApiClient } from './client'
import { normalizeIndicators, indicatorsToCsv, useIndicatorDataForGraph } from '@/adapters'
import { useLiveRef, hasQueryKeys } from '../../utils/helpers'
import { typeMapWithIds } from '@/api/Intel/utils'

export const useQuery = (options = {}) => {
  const { adapters, adapter: adapterName, ...queryOptions } = options
  const { data: rawData, ...result } = useQueryBase(queryOptions)
  const adapterRef = useLiveRef(adapters ? adapters[adapterName] : null)
  const data = useMemo(() => {
    if (rawData === undefined || adapterRef.current === undefined) { return }
    return adapterRef.current?.(rawData) || rawData
  }, [rawData, adapterRef])
  return { data, ...result }
}

const adapters = {
  default: normalizeIndicators,
  csv: indicatorsToCsv,
  raw: (data) => data,
  graphData: useIndicatorDataForGraph
}

export const useIndicatorDownloadQuery = (type, options = {}) => {
  if (!type) {
    throw new Error('useIndicatorDownloadQuery requires a type')
  }
  const api = useMatiApiClient('indicators')
  const { ids, ...restOptions } = options

  if (ids && !Array.isArray(ids)) {
    throw new Error('ids option must be an array')
  }

  const enabled = !!type && !!ids?.length

  return useQuery({
    ...options,
    queryFn: () => api.download(typeMapWithIds(type, ids)),
    queryKey: ['indicators', 'download', type, ids],
    enabled: (restOptions.enabled ?? true) && enabled
  })
}

export const useIndicatorFetch = (query, options = {}) => {
  const api = useMatiApiClient('indicators')
  const enabled = !!query

  return useQuery({
    queryFn: () => api.fetchIndicator(query),
    queryKey: ['indicators', query],
    refetchOnWindowFocus: false,
    ...options,
    enabled: (options.enabled ?? true) && enabled,
  })
}

export const useIndicatorTestQuery = (query, options = {}) => {
  const api = useMatiApiClient('indicators')
  const enabled = hasQueryKeys({ query, keys: ['id', 'type'] })

  return useQuery({
    queryFn: () => api.testIndicators(query),
    queryKey: ['indicators', 'test', query],
    staleTime: 1000 * 60 * 10,
    ...options,
    enabled: (options.enabled ?? true) && enabled,
  })
}

export const useFilteredIndicatorsByTypeAndId = (query, options = {}) => {
  const api = useMatiApiClient('indicators')
  const enabled = hasQueryKeys({ query, keys: ['id', 'type'] })
  query.search = query.search && !query.search.endsWith('*') ? query.search + '*' : query.search
  query.attribution = query.attribution
    ? query.attribution?.map(attribute => attribute.endsWith('*') ? attribute : attribute + '*')
    : query.attribution

  return useQuery({
    queryFn: () => api.fetchFilteredIndicatorsByTypeAndId(query),
    queryKey: ['indicators', query],
    adapters,
    ...options,
    enabled: (options.enabled ?? true) && enabled,
  })
}

export const useActorIndicatorsFetch = (id, options = {}) => {
  const api = useMatiApiClient('indicators')
  const enabled = !!id

  return useQuery({
    queryFn: () => api.fetchActorIndicators(id),
    queryKey: ['indicators', id],
    adapters,
    ...options,
    enabled: (options.enabled ?? true) && enabled,
  })
}

export const useCampaignIndicatorsFetch = (id, options = {}) => {
  const api = useMatiApiClient('indicators')
  const enabled = !!id

  return useQuery({
    queryFn: () => api.fetchCampaignIndicators({ id, options }),
    queryKey: ['indicators', id, options],
    adapters,
    ...options,
    enabled: (options.enabled ?? true) && enabled,
  })
}

export const useMalwareIndicatorsFetch = (id, options = {}) => {
  const api = useMatiApiClient('indicators')
  const enabled = !!id

  return useQuery({
    queryFn: () => api.fetchMalwareIndicators({ id, options }),
    queryKey: ['indicators', id],
    adapters,
    ...options,
    enabled: (options.enabled ?? true) && enabled,
  })
}
